import React, { useEffect, useState } from 'react';
import { billingService } from '../../api/services/billing.service';
import { ConvertPreviewDTO, CreditAccountDTO, formatMoney } from '../../types/billing';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import { convertDecision, istNextMidnight, istToServerUtc } from './closeBooksRules';

interface Props {
  account: CreditAccountDTO;
  onClose: () => void;
  onDone: (message: string) => void;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500';

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className="text-sm font-medium text-gray-900 dark:text-white">{children}</p>
  </div>
);

/**
 * The last step of BILLING.md §3: postpaid → prepaid as of a cut-off (default: the next
 * IST midnight). The preview runs first and says what stands in the way: uninvoiced
 * usage or an unpaid usage invoice. When the only obstacle is usage that was invoiced
 * OUTSIDE cpass, the operator may convert with a reference to that invoicing; cpass then
 * marks the earlier usage settled and creates no tax invoice of its own.
 */
const ConvertToPrepaidModal: React.FC<Props> = ({ account, onClose, onDone }) => {
  const [asOfIst, setAsOfIst] = useState(istNextMidnight());
  const [preview, setPreview] = useState<ConvertPreviewDTO | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [settledOutside, setSettledOutside] = useState(false);
  const [reference, setReference] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const asOf = istToServerUtc(asOfIst);
  const name = account.organizationName || account.organizationId;
  const orgId = account.organizationId;

  useEffect(() => {
    if (!asOf) {
      setPreview(null);
      return;
    }
    let live = true;
    setPreviewing(true);
    billingService.getConvertToPrepaidPreview(orgId, asOf).then((r) => {
      if (!live) return;
      setPreviewing(false);
      if (r.success && r.data) {
        setPreview(r.data);
        setPreviewError(null);
      } else {
        setPreview(null);
        setPreviewError(r.error || 'Failed to preview the conversion');
      }
    });
    return () => {
      live = false;
    };
  }, [orgId, asOf]);

  const decision = convertDecision(preview, settledOutside, reference);
  const currency = preview?.currency || account.currency || 'INR';
  const money = (v?: number | null) => formatMoney(v ?? 0, currency);
  const when = asOfIst.replace('T', ' ');

  const convert = async () => {
    if (!asOf || !decision.allowed) return;
    setBusy(true);
    setError(null);
    const r = await billingService.convertToPrepaid(orgId, asOf, decision.reference);
    setBusy(false);
    setConfirming(false);
    if (!r.success) {
      setError(r.error || 'Failed to convert to prepaid');
      return;
    }
    onDone(
      `${name} is now PREPAID as of ${when} IST, balance ${formatMoney(
        r.data?.creditBalance,
        r.data?.currency || currency,
      )}.${decision.reference ? ` Earlier usage marked settled outside cpass (${decision.reference}).` : ''}`
        + ' Record the opening top-up next.',
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-semibold mb-1 dark:text-white">Convert to prepaid</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {name} · balance {formatMoney(account.creditBalance, account.currency)}
          </p>
          <ol className="text-sm text-gray-600 dark:text-gray-300 list-decimal ml-5 space-y-1 mb-4">
            <li>Invoice usage up to this cut-off, finalize it, and record its payment in full.</li>
            <li>Convert here with the same cut-off. The credit limit is removed.</li>
            <li>Record the opening top-up (Payments → add credits).</li>
          </ol>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (asOf && decision.allowed) setConfirming(true);
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="ctp-asof">
                Prepaid from (IST) *
              </label>
              <input
                id="ctp-asof"
                type="datetime-local"
                required
                value={asOfIst}
                onChange={(e) => setAsOfIst(e.target.value)}
                className={inputCls}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Usage from this instant is prepaid.{asOf ? ` Sent as ${asOf.replace('T', ' ')} UTC.` : ''}
              </p>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2" data-testid="convert-preview">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Before converting</p>
              {previewError ? (
                <p className="text-sm text-red-600">{previewError}</p>
              ) : !preview || previewing ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Checking…</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Fact label="Balance now">{money(preview.balance)}</Fact>
                    <Fact label="Uninvoiced usage before the cut-off">
                      {money(preview.uninvoicedAmount)} · {preview.uninvoicedCharges} charge(s)
                    </Fact>
                    <Fact label="Unpaid usage invoices">{preview.openUsageInvoices}</Fact>
                    <Fact label="Balance after settlement">{money(preview.balanceAfterSettlement)}</Fact>
                  </div>
                  {preview.canConvert ? (
                    <p className="text-sm text-green-700 dark:text-green-400">Ready to convert.</p>
                  ) : (
                    <p className="text-sm text-orange-700 dark:text-orange-300">{decision.reason}</p>
                  )}
                </>
              )}
            </div>

            {decision.offerOutside && (
              <div className="border border-orange-300 dark:border-orange-700 rounded-lg p-3 space-y-2" data-testid="outside-settlement">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={settledOutside} onChange={(e) => setSettledOutside(e.target.checked)} />
                  Earlier usage was invoiced outside cpass
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Marks the usage before the cut-off as settled in cpass. No cpass tax invoice is created for it: the
                  invoice you name here is the record.
                </p>
                {settledOutside && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="ctp-ref">
                      Outside invoice reference *
                    </label>
                    <input
                      id="ctp-ref"
                      required
                      value={reference}
                      placeholder="GG finance invoices to 31 Oct 2026"
                      onChange={(e) => setReference(e.target.value)}
                      className={inputCls}
                    />
                  </div>
                )}
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button type="submit" disabled={!asOf || !decision.allowed} className="tl-btn tl-btn-primary disabled:opacity-50">
                Convert…
              </button>
            </div>
          </form>
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={convert}
        isLoading={busy}
        variant="danger"
        title={`Move ${name} to prepaid?`}
        message={
          `From ${when} IST its credit limit is removed and calls draw on a prepaid balance.`
          + (decision.reference
            ? ` Usage before then is marked settled outside cpass ("${decision.reference}"); no cpass tax invoice is created for it.`
            : '')
        }
        confirmText="Convert to prepaid"
      />
    </>
  );
};

export default ConvertToPrepaidModal;
